import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  clearShopifyEmbeddedContext,
  buildEmbeddedAppPath,
  getShopifyEmbeddedContext,
  isShopifyEmbeddedRoute,
  setShopifyEmbeddedContext,
} from "@/lib/shopifyEmbedded";
import { clearShopifyAppBridge, ensureShopifyAppBridge } from "@/lib/shopifyAppBridge";

function ShopifyEmbeddedBridge() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const context = getShopifyEmbeddedContext(searchParams);
    const hasExplicitEmbeddedSignal =
      searchParams.get("embedded") === "1" ||
      searchParams.has("host") ||
      searchParams.has("shop") ||
      searchParams.has("id_token");

    if (!hasExplicitEmbeddedSignal && !context.embedded) {
      clearShopifyEmbeddedContext();
      clearShopifyAppBridge();
      document.documentElement.dataset.shopifyEmbedded = "false";
      return;
    }

    if (context.embedded && context.host && context.shop) {
      setShopifyEmbeddedContext(context);
      document.documentElement.dataset.shopifyEmbedded = "true";
      ensureShopifyAppBridge(import.meta.env.VITE_SHOPIFY_API_KEY);
      return;
    }

    clearShopifyEmbeddedContext();
    clearShopifyAppBridge();
    document.documentElement.dataset.shopifyEmbedded = "false";
  }, [searchParams]);

  useEffect(() => {
    if (!isShopifyEmbeddedRoute(location.pathname)) {
      return;
    }

    const context = getShopifyEmbeddedContext(searchParams);
    if (!context.host || !context.shop || searchParams.get("host")) {
      return;
    }

    const nextPath = buildEmbeddedAppPath(`${location.pathname}${location.search}${location.hash}`);
    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    if (nextPath !== currentPath) {
      navigate(nextPath, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate, searchParams]);

  return null;
}

export default ShopifyEmbeddedBridge;
